import { useState, useMemo, useCallback, type Dispatch, type SetStateAction } from "react";
import type { useTranslations } from "next-intl";
import type { Tier, TierId } from "@mw/shared/pricing";

import { formatNumberToKM } from "@utilities/numbers";
import { PRICING_TIERS, TIERS, getAgentIncludedMinutes } from "@mw/shared/pricing";

// Feature IDs that can be selected in the calculator (default variant)
export type CalculatorFeatureId =
  | "text_to_speech"
  | "speech_to_text"
  | "conversational_ai"
  | "instant_voice_cloning"
  | "professional_voice_cloning"
  | "music"
  | "dubbing"
  | "dubbing_studio"
  | "sound_effects"
  | "voice_isolator"
  | "commercial_license"
  | "no_attribution"
  | "multiple_seats"
  | "usage_analytics"
  | "audio_native"
  | "higher_concurrency";

// Feature IDs for the agents variant
export type AgentsFeatureId =
  | "agents_workflow_builder"
  | "agents_tool_calling"
  | "agents_guardrails"
  | "agents_knowledge_base"
  | "agents_multilingual"
  | "agents_widget"
  | "agents_text_messages"
  | "agents_concurrency_10"
  | "agents_concurrency_20"
  | "agents_concurrency_30"
  | "agents_professional_voice_cloning"
  | "agents_workspace_seats";

export interface CalculatorFeature<T extends string = CalculatorFeatureId> {
  id: T;
  category: FeatureCategory;
  /** Minimum tier required for this feature (derived from TIERS) */
  minTier: TierId;
}

export type FeatureCategory = "product" | "capability" | "included";

/** Definition for a calculator feature */
interface FeatureDefinition<T extends string> {
  id: T;
  category: FeatureCategory;
  isEnabled: (tier: Tier) => boolean;
}

// Feature definitions - labels are looked up from translations: calculator.features.{id}
export const FEATURE_DEFINITIONS: FeatureDefinition<CalculatorFeatureId>[] = [
  {
    id: "text_to_speech",
    category: "product",
    isEnabled: tier => tier.products.textToSpeech.enabled,
  },
  {
    id: "speech_to_text",
    category: "product",
    isEnabled: tier => tier.products.speechToText.enabled,
  },
  {
    id: "conversational_ai",
    category: "product",
    isEnabled: tier => tier.products.conversationalAI.enabled,
  },
  {
    id: "instant_voice_cloning",
    category: "capability",
    isEnabled: tier => tier.products.voices.features.instantVoiceCloning,
  },
  {
    id: "professional_voice_cloning",
    category: "capability",
    isEnabled: tier => tier.products.voices.features.professionalVoiceCloning,
  },
  {
    id: "music",
    category: "product",
    isEnabled: tier => tier.products.music.enabled,
  },
  {
    id: "dubbing",
    category: "product",
    isEnabled: tier => tier.products.dubbing.enabled,
  },
  {
    id: "dubbing_studio",
    category: "product",
    isEnabled: tier => tier.products.dubbing.features.studio,
  },
  {
    id: "sound_effects",
    category: "product",
    isEnabled: tier => tier.products.soundEffects.enabled,
  },
  {
    id: "voice_isolator",
    category: "product",
    isEnabled: tier => tier.products.voiceIsolator.enabled,
  },
  {
    id: "commercial_license",
    category: "capability",
    isEnabled: tier => tier.capabilities.commercialLicense,
  },
  {
    id: "no_attribution",
    category: "capability",
    isEnabled: tier => tier.capabilities.noAttributionRequired,
  },
  {
    id: "multiple_seats",
    category: "capability",
    isEnabled: tier => tier.capabilities.multipleSeats,
  },
  {
    id: "usage_analytics",
    category: "capability",
    isEnabled: tier => tier.capabilities.usageAnalytics,
  },
  {
    id: "audio_native",
    category: "product",
    isEnabled: tier => tier.products.audioNative.enabled,
  },
  {
    id: "higher_concurrency",
    category: "capability",
    isEnabled: tier => tier.products.textToSpeech.limits.concurrencyLimit > 2,
  },
];

/** Tiers available for recommendation (excluding enterprise and PAYG) */
export function getSelectableTiers(pricingTiers: Tier[]): Tier[] {
  return pricingTiers.filter(t => t.planId !== "enterprise" && t.planId !== "payg");
}

// Legacy constant — kept for backward compatibility
const SELECTABLE_TIERS = getSelectableTiers(PRICING_TIERS);

// Agent feature definitions - labels are looked up from translations: calculator.agents_features.{id}
export const AGENT_FEATURE_DEFINITIONS: FeatureDefinition<AgentsFeatureId>[] = [
  // Included features (available on all tiers)
  {
    id: "agents_workflow_builder",
    category: "included",
    isEnabled: () => true,
  },
  {
    id: "agents_tool_calling",
    category: "included",
    isEnabled: () => true,
  },
  {
    id: "agents_guardrails",
    category: "included",
    isEnabled: () => true,
  },
  {
    id: "agents_knowledge_base",
    category: "included",
    isEnabled: () => true,
  },
  {
    id: "agents_multilingual",
    category: "included",
    isEnabled: () => true,
  },
  {
    id: "agents_widget",
    category: "included",
    isEnabled: () => true,
  },
  {
    id: "agents_text_messages",
    category: "capability",
    isEnabled: () => true,
  },
  {
    id: "agents_concurrency_10",
    category: "capability",
    isEnabled: tier => tier.platformConfig.agents.concurrencyLimit >= 10,
  },
  {
    id: "agents_concurrency_20",
    category: "capability",
    isEnabled: tier => tier.platformConfig.agents.concurrencyLimit >= 20,
  },
  {
    id: "agents_concurrency_30",
    category: "capability",
    isEnabled: tier => tier.platformConfig.agents.concurrencyLimit >= 30,
  },
  {
    id: "agents_professional_voice_cloning",
    category: "capability",
    isEnabled: tier => tier.products.voices.features.professionalVoiceCloning,
  },
  {
    id: "agents_workspace_seats",
    category: "capability",
    isEnabled: tier => tier.capabilities.multipleSeats,
  },
];

// Credits per minute of generated audio. ElevenCreative non-turbo TTS is 1 credit/character
// at the industry-standard ~1000 chars/minute → 1000 credits/minute. (Was 500, the deprecated
// 0.5-credit/char rate, which over-stated the minutes each plan covers — W-2223.)
export const CREDITS_PER_MINUTE = 1000;

/**
 * Max creative-platform minutes for the slider, derived from the Business tier's included credits.
 */
export function getBusinessMaxMinutes(tiersById: Record<TierId, Tier>): number {
  return tiersById.scale_2024_08_10.pricing.includedCredits / CREDITS_PER_MINUTE;
}

/**
 * Agent minutes included per tier — checks platformConfig.agents first (V2), falls back to legacy.
 */
export function getAgentMinutesByTier(pricingTiers: Tier[]): Record<TierId, number> {
  return pricingTiers.reduce(
    (acc, tier) => {
      acc[tier.planId] = getAgentIncludedMinutes(tier);
      return acc;
    },
    {} as Record<TierId, number>,
  );
}

/**
 * Max agent minutes for the slider (Business tier).
 * Checks platformConfig.agents first (V2), falls back to legacy conversationalAI limits.
 */
export function getAgentsMaxMinutes(tiersById: Record<TierId, Tier>): number {
  return getAgentIncludedMinutes(tiersById.scale_2024_08_10);
}

const BUSINESS_MAX_MINUTES = getBusinessMaxMinutes(TIERS);
const AGENT_MINUTES_BY_TIER: Record<TierId, number> = getAgentMinutesByTier(PRICING_TIERS);

// Slider configuration
export const SLIDER_MIN = 0;
export const SLIDER_MAX = 100;

/**
 * Convert slider position (0-100) to a usage value using exponential scale.
 * Gives more precision at lower values where most users will be.
 * Rounds to nice numbers across multiple orders of magnitude so the same
 * helper works for minutes, hours, characters, etc.
 */
export function sliderToValue(sliderValue: number, maxValue: number = BUSINESS_MAX_MINUTES): number {
  if (sliderValue === 0) return 0;
  if (sliderValue >= 100) return maxValue;

  const minValue = 1;
  const ratio = maxValue / minValue;
  const value = minValue * Math.pow(ratio, sliderValue / 100);

  // Round to nice numbers across orders of magnitude
  if (value < 10) return Math.round(value);
  if (value < 100) return Math.round(value / 5) * 5;
  if (value < 500) return Math.round(value / 10) * 10;
  if (value < 1000) return Math.round(value / 50) * 50;
  if (value < 10_000) return Math.round(value / 100) * 100;
  if (value < 100_000) return Math.round(value / 1000) * 1000;
  if (value < 1_000_000) return Math.round(value / 10_000) * 10_000;
  return Math.round(value / 100_000) * 100_000;
}

/** Convert a usage value to slider position (0-100). Inverse of sliderToValue. */
export function valueToSlider(value: number, maxValue: number = BUSINESS_MAX_MINUTES): number {
  if (value <= 0) return 0;
  if (value >= maxValue) return 100;

  const minValue = 1;
  const ratio = maxValue / minValue;
  const sliderValue = (Math.log(value / minValue) / Math.log(ratio)) * 100;

  return Math.max(0, Math.min(100, sliderValue));
}

/** Convert minutes to credits for tier comparison */
export function minutesToCredits(minutes: number): number {
  return minutes * CREDITS_PER_MINUTE;
}

function getTierRank(tierId: TierId, pricingTiers: Tier[]): number {
  return pricingTiers.findIndex(tier => tier.planId === tierId);
}

/**
 * Derives calculator features with their minimum required tier from feature definitions.
 * Pass `pricingTiers` to use V2 data; defaults to legacy PRICING_TIERS.
 */
export function deriveFeatures<T extends string>(
  definitions: FeatureDefinition<T>[],
  pricingTiers: Tier[] = PRICING_TIERS,
): CalculatorFeature<T>[] {
  return definitions.map(def => {
    let minTier: TierId = "enterprise";

    for (const tier of pricingTiers) {
      if (def.isEnabled(tier)) {
        minTier = tier.planId;
        break;
      }
    }

    return { id: def.id, category: def.category, minTier };
  });
}

const CALCULATOR_FEATURES = deriveFeatures(FEATURE_DEFINITIONS);
const AGENT_CALCULATOR_FEATURES = deriveFeatures(AGENT_FEATURE_DEFINITIONS);

function getMinTierForSelectedFeatures<T extends string>(
  selectedFeatures: T[],
  allFeatures: CalculatorFeature<T>[],
  selectableTiers: Tier[],
  pricingTiers: Tier[],
): Tier {
  let minTierRank = 0;
  for (const featureId of selectedFeatures) {
    const feature = allFeatures.find(f => f.id === featureId);
    if (feature) {
      const rank = getTierRank(feature.minTier, pricingTiers);
      if (rank > minTierRank) minTierRank = rank;
    }
  }

  for (const tier of selectableTiers) {
    if (getTierRank(tier.planId, pricingTiers) >= minTierRank) return tier;
  }

  return selectableTiers[selectableTiers.length - 1];
}

/**
 * Get the minimum required tier based on selected features.
 * Pass `pricingTiers` to use V2 data; defaults to legacy.
 */
export function getMinTierForFeatures(
  selectedFeatures: CalculatorFeatureId[],
  pricingTiers: Tier[] = PRICING_TIERS,
  calculatorFeatures: CalculatorFeature<CalculatorFeatureId>[] = CALCULATOR_FEATURES,
): Tier {
  return getMinTierForSelectedFeatures(
    selectedFeatures,
    calculatorFeatures,
    getSelectableTiers(pricingTiers),
    pricingTiers,
  );
}

/**
 * Get the minimum required tier based on selected agent features.
 * Pass `pricingTiers` to use V2 data; defaults to legacy.
 */
export function getMinTierForAgentFeatures(
  selectedFeatures: AgentsFeatureId[],
  pricingTiers: Tier[] = PRICING_TIERS,
  agentFeatures: CalculatorFeature<AgentsFeatureId>[] = AGENT_CALCULATOR_FEATURES,
): Tier {
  return getMinTierForSelectedFeatures(selectedFeatures, agentFeatures, getSelectableTiers(pricingTiers), pricingTiers);
}

/**
 * Calculate the recommended tier based on selected features and credit usage.
 * Pass `pricingTiers` to use V2 data; defaults to legacy.
 */
export function getRecommendedTier(
  selectedFeatures: CalculatorFeatureId[],
  creditUsage: number,
  pricingTiers: Tier[] = PRICING_TIERS,
  calculatorFeatures: CalculatorFeature<CalculatorFeatureId>[] = CALCULATOR_FEATURES,
): Tier {
  const selectableTiers = getSelectableTiers(pricingTiers);
  const minTier = getMinTierForFeatures(selectedFeatures, pricingTiers, calculatorFeatures);
  const minTierRank = getTierRank(minTier.planId, pricingTiers);

  for (const tier of selectableTiers) {
    const tierRank = getTierRank(tier.planId, pricingTiers);
    if (tierRank >= minTierRank && tier.pricing.includedCredits >= creditUsage) return tier;
  }

  const scaleTier = pricingTiers.find(t => t.planId === "scale");
  const enterpriseTier = pricingTiers.find(t => t.planId === "enterprise");

  if (scaleTier && creditUsage > scaleTier.pricing.includedCredits) {
    return enterpriseTier || scaleTier;
  }

  return scaleTier || selectableTiers[selectableTiers.length - 1];
}

/** Format a numeric usage value with a trailing unit suffix (e.g. "500K characters", "50 h", "100 min"). */
export function formatUsage(value: number, suffix: string): string {
  if (value >= 1000) {
    return `${formatNumberToKM(value)} ${suffix}`;
  }
  return `${value.toLocaleString()} ${suffix}`;
}

export type PaygUnit = "characters" | "hours" | "minutes";

type PaygTranslate = ReturnType<typeof useTranslations<"PricingClientKeys.PaygCostCalculator">>;

export function getStepLabel(t: PaygTranslate, unit: PaygUnit) {
  if (unit === "characters") return t("slider_label_characters");
  if (unit === "hours") return t("slider_label_hours");
  return t("slider_label_minutes");
}

export function getUnitShort(t: PaygTranslate, unit: PaygUnit) {
  if (unit === "characters") return t("unit_short_characters");
  if (unit === "hours") return t("unit_short_hours");
  return t("unit_short_minutes");
}

export function getUnitLong(t: PaygTranslate, unit: PaygUnit) {
  if (unit === "characters") return t("unit_long_characters");
  if (unit === "hours") return t("unit_long_hours");
  return t("unit_long_minutes");
}

/**
 * Calculate the recommended tier based on agent call minutes and selected features.
 * Pass `pricingTiers` / `agentMinutesByTier` to use V2 data; defaults to legacy.
 */
export function getRecommendedTierForAgents(
  callMinutes: number,
  selectedFeatures: AgentsFeatureId[] = [],
  pricingTiers: Tier[] = PRICING_TIERS,
  agentFeatures: CalculatorFeature<AgentsFeatureId>[] = AGENT_CALCULATOR_FEATURES,
  agentMinutesByTier: Record<TierId, number> = AGENT_MINUTES_BY_TIER,
): Tier {
  const selectableTiers = getSelectableTiers(pricingTiers);
  const minTier = getMinTierForAgentFeatures(selectedFeatures, pricingTiers, agentFeatures);
  const minTierRank = getTierRank(minTier.planId, pricingTiers);
  const enterpriseTier = pricingTiers.find(t => t.planId === "enterprise");

  for (const tier of selectableTiers) {
    const tierRank = getTierRank(tier.planId, pricingTiers);
    const includedMinutes = agentMinutesByTier[tier.planId];
    if (tierRank >= minTierRank && includedMinutes >= callMinutes) return tier;
  }

  return enterpriseTier || selectableTiers[selectableTiers.length - 1];
}

// ---------------------------------------------------------------------------
// Shared hook for usage slider state
// ---------------------------------------------------------------------------

export interface UseUsageSliderReturn {
  sliderValue: number;
  setSliderValue: Dispatch<SetStateAction<number>>;
  value: number;
  handleSliderChange: (sliderValue: number) => void;
}

export function useUsageSlider(
  initialValue: number,
  maxValue: number,
  onValueChange?: (value: number) => void,
): UseUsageSliderReturn {
  const [sliderValue, setSliderValue] = useState(() => valueToSlider(initialValue, maxValue));
  const value = useMemo(() => sliderToValue(sliderValue, maxValue), [sliderValue, maxValue]);

  const handleSliderChange = useCallback(
    (next: number) => {
      setSliderValue(next);
      onValueChange?.(sliderToValue(next, maxValue));
    },
    [maxValue, onValueChange],
  );

  return { sliderValue, setSliderValue, value, handleSliderChange };
}
